import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import i18n from "i18next";
import { api } from "./api";
import safeStorage from "./safeStorage";

const AuthCtx = createContext(null);

const logErr = (msg, e) => {
  if (process.env.NODE_ENV !== "production" && !e?.silent) {
    console.error(msg, e);
  }
};

const PREVIEW_LANG = "en";

// Synchronise i18n with the active user's preferred_language. For real users
// this honours whatever they chose in Account Settings (and is persisted by
// PATCH /api/auth/language). For PREVIEW accounts (Sofia + concierge + host
// demo profiles) we force English every time so a previous visitor who flipped
// the language picker — or a stale write from a test run — can't leak into
// the next preview visitor.
const syncLang = (u) => {
  if (!u) return;
  if (u.preview) {
    // Force the preview UI back to English. Also clear the persisted detector
    // cache (`trel-lang` localStorage key) so a hard refresh while still in
    // preview also returns to EN.
    safeStorage.remove("trel-lang");
    if (i18n.language !== PREVIEW_LANG) {
      i18n.changeLanguage(PREVIEW_LANG);
    }
    return;
  }
  if (u.preferred_language && u.preferred_language !== i18n.language) {
    i18n.changeLanguage(u.preferred_language);
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Admin/Owner permission profile (loaded lazily via fetchAdminPerms())
  const [adminPerms, setAdminPerms] = useState(null);

  const fetchAdminPerms = useCallback(async (u) => {
    if (!u || u.role !== "admin") { setAdminPerms(null); return null; }
    try {
      const { data } = await api.get("/admin/me/permissions");
      setAdminPerms(data);
      return data;
    } catch (e) {
      logErr("admin/me/permissions error", e);
      setAdminPerms(null);
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      syncLang(data);
      if (data?.role === "admin") {
        await fetchAdminPerms(data);
      } else {
        setAdminPerms(null);
      }
    } catch (e) {
      logErr("auth/me error", e);
      setUser(null);
      setAdminPerms(null);
    }
    setLoading(false);
  }, [fetchAdminPerms]);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip /me check
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user); syncLang(data.user);
    if (data.user?.role === "admin") await fetchAdminPerms(data.user);
    return data.user;
  };
  const demoLogin = async (role) => {
    const { data } = await api.post(`/demo/login/${role}`);
    if (data?.preview_visitor_id) {
      // 24h TTL — preview visitor analytics only; not security-sensitive but
      // we don't want it to linger in browsers forever.
      safeStorage.setWithExpiry("trel_preview_visitor", data.preview_visitor_id, 24 * 60 * 60 * 1000);
    }
    setUser(data.user); syncLang(data.user);
    if (data.user?.role === "admin") await fetchAdminPerms(data.user);
    return data.user;
  };
  const signup = async (payload) => {
    const visitor = safeStorage.getWithExpiry("trel_preview_visitor");
    const headers = visitor ? { "X-Trel-Preview-Visitor": visitor } : undefined;
    const { data } = await api.post("/auth/signup", payload, { headers });
    safeStorage.remove("trel_preview_visitor");
    setUser(data.user); syncLang(data.user);
    if (data.user?.role === "admin") await fetchAdminPerms(data.user);
    return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (e) { logErr("logout error", e); }
    setUser(null);
    setAdminPerms(null);
  };

  // Helper: does the logged-in user hold a given admin permission?
  const hasPermission = (perm) => {
    if (!user) return false;
    if (adminPerms?.is_owner) return true;
    return (adminPerms?.permissions || []).includes(perm);
  };
  const isOwner = () => Boolean(adminPerms?.is_owner);

  const value = {
    user, loading, login, demoLogin, signup, logout,
    refresh: checkAuth, setUser,
    adminPerms, refreshPerms: () => fetchAdminPerms(user),
    hasPermission, isOwner,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
export const useAuth = () => useContext(AuthCtx);
